import { Action, Mutation, Module, VuexModule } from "vuex-module-decorators";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import SkillsSimulationService from "@/core/services/SkillsSimulationService";
import type { SubmitSkillsSimulationAnswerPayload } from "@/core/services/SkillsSimulationService";

/**
 * El backend (Seccuri.Shared.Results.Result<T>) serializa los errores como
 * `errors: [{ error: string, code: string | null }]`, no como strings
 * sueltos — hay que extraer `.error` de cada uno. Verificado leyendo
 * Result.cs/ResultError.cs en el repo SeccuriBackend (mismo patrón que
 * usan talent-assessment/* y presumiblemente el módulo nuevo).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractErrorMessages(response: any, fallback: string): Array<string> {
  const errors = response?.data?.errors;
  if (Array.isArray(errors) && errors.length) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return errors.map((e: any) => e?.error ?? e?.message ?? String(e));
  }
  return [fallback];
}

/**
 * Simulación de Pruebas Técnicas (talento de ciberseguridad).
 *
 * Módulo Vuex aislado (mismo patrón que AiRmfModule/BodyModule/
 * BreadcrumbsModule/ConfigModule) migrado desde el prototipo original en
 * Pinia (stores/assessment.js). A diferencia del prototipo, la sesión de
 * evaluación queda ligada al talento autenticado: el backend resuelve el
 * talent desde el JWT (rol Talent) al crear la sesión — no se envía
 * profileId ni se crea una sesión anónima. START_SKILLS_SIMULATION es
 * idempotente por (talent, skill): un talent tiene una sola sesión de por
 * vida POR SKILL, así que puede dispatch-earse de forma segura en cada
 * mount de la vista de menú (si ya existe, el backend devuelve la sesión
 * existente en vez de crear una nueva).
 *
 * MULTI-SKILL: este módulo solo mantiene el estado de UN skill activo a
 * la vez (el que está montado en pantalla — ver `currentSkill`), no una
 * colección de sesiones en memoria. Al cambiar de skill (navegación entre
 * /talent/skills-simulation/:skill distintos) las vistas vuelven a
 * dispatch-ear START_SKILLS_SIMULATION con el nuevo skill, que reemplaza
 * el estado por completo — cada skill tiene su propio progreso en el
 * backend, pero el frontend no necesita tenerlos todos cargados a la vez.
 *
 * Las llamadas HTTP viven en SkillsSimulationService (no se llama a
 * ApiService directamente desde aquí).
 */

export type SkillsSimulationTestStatus = "Locked" | "Available" | "Passed";

export type SkillsSimulationQuestionType =
  | "Classify"
  | "MultipleChoiceSet"
  | "Reorder"
  | "MultiSelect"
  | "SingleChoiceRubric";

export interface SkillsSimulationTest {
  category: string;
  categoryName: string;
  complexity: number; // 1-5
  status: SkillsSimulationTestStatus;
  attempts: number;
  finalScore: number | null;
}

export interface SkillsSimulationClassifyItem {
  id: string;
  text: string;
}

export interface SkillsSimulationChoiceSubQuestion {
  id: string;
  scenarioText: string;
  questionText: string;
  options: Array<string>;
}

export interface SkillsSimulationReorderItem {
  id: string;
  text: string;
}

export interface SkillsSimulationSelectOption {
  id: string;
  text: string;
}

export interface SkillsSimulationRubricOption {
  id: string;
  text: string;
}

export interface SkillsSimulationQuestion {
  id: string;
  category: string;
  complexity: number;
  type: SkillsSimulationQuestionType;
  title: string;
  scenario: string;
  classifyItems?: Array<SkillsSimulationClassifyItem>;
  choiceSubQuestions?: Array<SkillsSimulationChoiceSubQuestion>;
  reorderItems?: Array<SkillsSimulationReorderItem>;
  selectOptions?: Array<SkillsSimulationSelectOption>;
  rubricOptions?: Array<SkillsSimulationRubricOption>;
}

export interface SkillsSimulationResult {
  score: number;
  passed: boolean;
  feedback: string;
}

export interface SkillsSimulationSummaryRow {
  category: string;
  categoryName: string;
  complexity: number;
  attempts: number;
  score: number;
}

export interface SkillsSimulationSummary {
  sessionId: string;
  finalScore: number;
  expertiseScore: number;
  // Nombre del nivel tal como lo envía el backend: "Basic" | "Intermediate"
  // | "Advanced" | "Expert" (ver mapeo de etiqueta en SkillsSimulationResults.vue).
  expertiseLevel: string;
  results: Array<SkillsSimulationSummaryRow>;
  // ISO 8601 — momento en que se aprobó la 5ta prueba.
  completedDate: string;
}

// Una entrada del historial de intentos de un skill (GET
// /skills-simulation/history?skill=). Los campos de score/nivel vienen
// null mientras el intento sigue incompleto — solo el más reciente
// (attempts[0]) puede estar incompleto, ya que el backend no permite
// crear un intento nuevo mientras el anterior no esté completo.
export interface SkillsSimulationHistoryAttempt {
  sessionId: string;
  allPassed: boolean;
  finalScore: number | null;
  expertiseScore: number | null;
  expertiseLevel: string | null;
  createdDate: string;
  completedDate: string | null;
}

interface SkillsSimulationHistoryResponse {
  skill: string;
  attempts: Array<SkillsSimulationHistoryAttempt>;
}

interface StartSkillsSimulationResponse {
  sessionId: string;
  tests: Array<SkillsSimulationTest>;
  allPassed: boolean;
}

interface SkillsSimulationSessionMutationPayload
  extends StartSkillsSimulationResponse {
  // No viene del backend — lo agrega la acción START_SKILLS_SIMULATION a
  // partir del payload con el que fue dispatch-eada, para que el módulo
  // sepa a qué skill pertenece la sesión activa.
  skill: string;
}

interface SkillsSimulationMenuResponse {
  tests: Array<SkillsSimulationTest>;
  allPassed: boolean;
}

interface SubmitSkillsSimulationAnswerResponse {
  score: number;
  passed: boolean;
  feedback: string;
  menu: SkillsSimulationMenuResponse;
  retryQuestion?: SkillsSimulationQuestion | null;
}

interface SkillsSimulationState {
  currentSkill: string | null;
  sessionId: string | null;
  tests: Array<SkillsSimulationTest>;
  allPassed: boolean;
  currentQuestion: SkillsSimulationQuestion | null;
  pendingRetryQuestion: SkillsSimulationQuestion | null;
  lastResult: SkillsSimulationResult | null;
  summary: SkillsSimulationSummary | null;
  // Historial de intentos del skill activo (más reciente primero). Se
  // carga junto con ENSURE_SKILLS_SIMULATION_SESSION; ver getter
  // currentSkillsSimulationHistory.
  history: Array<SkillsSimulationHistoryAttempt>;
  loading: boolean;
  errors: Array<string>;
}

@Module
export default class SkillsSimulationModule
  extends VuexModule
  implements SkillsSimulationState
{
  currentSkill: string | null = null;
  sessionId: string | null = null;
  tests: Array<SkillsSimulationTest> = [];
  allPassed = false;
  currentQuestion: SkillsSimulationQuestion | null = null;
  pendingRetryQuestion: SkillsSimulationQuestion | null = null;
  lastResult: SkillsSimulationResult | null = null;
  summary: SkillsSimulationSummary | null = null;
  history: Array<SkillsSimulationHistoryAttempt> = [];
  loading = false;
  errors: Array<string> = [];

  get currentSkillsSimulationSkill(): string | null {
    return this.currentSkill;
  }

  get currentSkillsSimulationSessionId(): string | null {
    return this.sessionId;
  }

  get currentSkillsSimulationTests(): Array<SkillsSimulationTest> {
    return this.tests;
  }

  get currentSkillsSimulationAllPassed(): boolean {
    return this.allPassed;
  }

  get currentSkillsSimulationAvailableTest(): SkillsSimulationTest | undefined {
    return this.tests.find((t) => t.status === "Available");
  }

  get currentSkillsSimulationQuestion(): SkillsSimulationQuestion | null {
    return this.currentQuestion;
  }

  get currentSkillsSimulationLastResult(): SkillsSimulationResult | null {
    return this.lastResult;
  }

  get currentSkillsSimulationSummary(): SkillsSimulationSummary | null {
    return this.summary;
  }

  get currentSkillsSimulationHistory(): Array<SkillsSimulationHistoryAttempt> {
    return this.history;
  }

  get currentSkillsSimulationLoading(): boolean {
    return this.loading;
  }

  get currentSkillsSimulationErrors(): Array<string> {
    return this.errors;
  }

  @Mutation
  [Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION](payload: boolean) {
    this.loading = payload;
  }

  @Mutation
  [Mutations.SET_SKILLS_SIMULATION_ERROR_MUTATION](payload: Array<string>) {
    this.errors = payload;
  }

  @Mutation
  [Mutations.SET_SKILLS_SIMULATION_SESSION_MUTATION](
    payload: SkillsSimulationSessionMutationPayload,
  ) {
    this.currentSkill = payload.skill;
    this.sessionId = payload.sessionId;
    this.tests = payload.tests;
    this.allPassed = payload.allPassed;
    this.currentQuestion = null;
    this.pendingRetryQuestion = null;
    this.lastResult = null;
    this.summary = null;
  }

  @Mutation
  [Mutations.SET_SKILLS_SIMULATION_MENU_MUTATION](
    payload: SkillsSimulationMenuResponse,
  ) {
    this.tests = payload.tests;
    this.allPassed = payload.allPassed;
  }

  @Mutation
  [Mutations.SET_SKILLS_SIMULATION_QUESTION_MUTATION](
    payload: SkillsSimulationQuestion | null,
  ) {
    this.currentQuestion = payload;
    this.lastResult = null;
    this.pendingRetryQuestion = null;
  }

  @Mutation
  [Mutations.SET_SKILLS_SIMULATION_RESULT_MUTATION](
    payload: SubmitSkillsSimulationAnswerResponse,
  ) {
    this.lastResult = {
      score: payload.score,
      passed: payload.passed,
      feedback: payload.feedback,
    };
    this.tests = payload.menu.tests;
    this.allPassed = payload.menu.allPassed;

    if (payload.passed) {
      // OJO: no limpiar currentQuestion acá. La vista (Runner.vue) usa
      // "question && lastResult" para mostrar el mensaje de score/feedback
      // y el botón "Continuar a la siguiente prueba" — si se limpia acá,
      // la vista cae directo al estado "prueba no disponible" y el
      // talento nunca ve que aprobó (bug real reportado por Andres).
      // currentQuestion se limpia solo cuando se carga la próxima
      // pregunta (SET_SKILLS_SIMULATION_QUESTION_MUTATION) o al salir de
      // la vista.
      this.pendingRetryQuestion = null;
    } else {
      // Se guarda la nueva variante, pero no se muestra hasta que el
      // talento decida reintentar (para no cambiar el enunciado bajo la
      // respuesta que acaba de calificarse) — mismo comportamiento que el
      // prototipo.
      this.pendingRetryQuestion = payload.retryQuestion ?? null;
    }
  }

  @Mutation
  [Mutations.APPLY_SKILLS_SIMULATION_RETRY_MUTATION]() {
    this.currentQuestion = this.pendingRetryQuestion;
    this.pendingRetryQuestion = null;
    this.lastResult = null;
  }

  @Mutation
  [Mutations.SET_SKILLS_SIMULATION_SUMMARY_MUTATION](
    payload: SkillsSimulationSummary,
  ) {
    this.summary = payload;
  }

  @Mutation
  [Mutations.SET_SKILLS_SIMULATION_HISTORY_MUTATION](
    payload: Array<SkillsSimulationHistoryAttempt>,
  ) {
    this.history = payload;
  }

  @Mutation
  [Mutations.SET_SKILLS_SIMULATION_COMPLETED_ATTEMPT_MUTATION](payload: {
    skill: string;
    sessionId: string;
  }) {
    // Se usa cuando ENSURE_SKILLS_SIMULATION_SESSION detecta, por el
    // historial, que el último intento del skill ya está completo. Deja
    // la sesión activa apuntando a ese intento (para poder pedir su
    // resumen con GET_SKILLS_SIMULATION_SUMMARY) sin volver a llamar
    // POST /session — a diferencia de SET_SKILLS_SIMULATION_SESSION_MUTATION,
    // acá no se conoce el detalle de `tests` (no hace falta: las vistas
    // redirigen a la pantalla de resultados en vez de mostrar el grid de
    // pruebas para un intento ya completo).
    this.currentSkill = payload.skill;
    this.sessionId = payload.sessionId;
    this.allPassed = true;
    this.tests = [];
    this.currentQuestion = null;
    this.pendingRetryQuestion = null;
    this.lastResult = null;
    this.summary = null;
  }

  @Mutation
  [Mutations.RESET_SKILLS_SIMULATION_MUTATION]() {
    this.currentSkill = null;
    this.sessionId = null;
    this.tests = [];
    this.allPassed = false;
    this.currentQuestion = null;
    this.pendingRetryQuestion = null;
    this.lastResult = null;
    this.summary = null;
    this.history = [];
    this.errors = [];
  }

  @Action
  [Actions.START_SKILLS_SIMULATION](payload: { skill: string }) {
    this.context.commit(Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION, true);
    return SkillsSimulationService.startSession(payload.skill)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_SKILLS_SIMULATION_SESSION_MUTATION, {
          ...data.data,
          skill: payload.skill,
        });
        return data.data as StartSkillsSimulationResponse;
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_ERROR_MUTATION,
          extractErrorMessages(
            response,
            "No se pudo iniciar la evaluación. Intente de nuevo.",
          ),
        );
        return undefined;
      })
      .finally(() => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION,
          false,
        );
      });
  }

  @Action
  [Actions.GET_SKILLS_SIMULATION_MENU]() {
    // Reanuda/refresca el estado de la sesión ya existente sin volver a
    // llamar START_SKILLS_SIMULATION. Disponible para flujos de
    // reconexión (p.ej. si en el futuro se persiste el sessionId entre
    // reloads); hoy SkillsSimulationMenu.vue resuelve el reload llamando
    // START_SKILLS_SIMULATION de nuevo, que es idempotente.
    if (!this.sessionId) return Promise.resolve(undefined);

    this.context.commit(Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION, true);
    return SkillsSimulationService.getMenu(this.sessionId)
      .then(({ data }) => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_MENU_MUTATION,
          data.data,
        );
        return data.data as SkillsSimulationMenuResponse;
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_ERROR_MUTATION,
          extractErrorMessages(
            response,
            "No se pudo actualizar el estado de la evaluación.",
          ),
        );
        return undefined;
      })
      .finally(() => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION,
          false,
        );
      });
  }

  @Action
  [Actions.GET_SKILLS_SIMULATION_QUESTION](payload: { category: string }) {
    if (!this.sessionId) return Promise.resolve(undefined);

    this.context.commit(Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION, true);
    return SkillsSimulationService.getActiveQuestion(
      this.sessionId,
      payload.category,
    )
      .then(({ data }) => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_QUESTION_MUTATION,
          data.data,
        );
        return data.data as SkillsSimulationQuestion;
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_ERROR_MUTATION,
          extractErrorMessages(
            response,
            "Esta prueba no está disponible todavía.",
          ),
        );
        return undefined;
      })
      .finally(() => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION,
          false,
        );
      });
  }

  @Action
  [Actions.SUBMIT_SKILLS_SIMULATION_ANSWER](payload: {
    category: string;
    answer: SubmitSkillsSimulationAnswerPayload;
  }) {
    if (!this.sessionId) return Promise.resolve(undefined);

    this.context.commit(Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION, true);
    return SkillsSimulationService.submitAnswer(
      this.sessionId,
      payload.category,
      payload.answer,
    )
      .then(({ data }) => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_RESULT_MUTATION,
          data.data,
        );
        return data.data as SubmitSkillsSimulationAnswerResponse;
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_ERROR_MUTATION,
          extractErrorMessages(
            response,
            "No se pudo calificar la prueba. Intente de nuevo.",
          ),
        );
        return undefined;
      })
      .finally(() => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION,
          false,
        );
      });
  }

  @Action
  [Actions.RETRY_SKILLS_SIMULATION_TEST]() {
    // Sin llamada HTTP: solo activa la variante ya recibida en el submit
    // anterior (pendingRetryQuestion).
    this.context.commit(Mutations.APPLY_SKILLS_SIMULATION_RETRY_MUTATION);
  }

  @Action
  [Actions.GET_SKILLS_SIMULATION_SUMMARY]() {
    if (!this.sessionId) return Promise.resolve(undefined);

    this.context.commit(Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION, true);
    return SkillsSimulationService.getSummary(this.sessionId)
      .then(({ data }) => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_SUMMARY_MUTATION,
          data.data,
        );
        return data.data as SkillsSimulationSummary;
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_ERROR_MUTATION,
          extractErrorMessages(
            response,
            "El resumen final aún no está disponible.",
          ),
        );
        return undefined;
      })
      .finally(() => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION,
          false,
        );
      });
  }

  @Action
  [Actions.GET_SKILLS_SIMULATION_HISTORY](payload: { skill: string }) {
    // Sin wrap de loading acá a propósito: hoy el único llamador es
    // ENSURE_SKILLS_SIMULATION_SESSION, que ya maneja el loading de todo
    // el flujo — envolverlo acá también solo produciría un parpadeo
    // false/true intermedio sin ningún beneficio visual.
    return SkillsSimulationService.getHistory(payload.skill)
      .then(({ data }) => {
        const response = data.data as SkillsSimulationHistoryResponse;
        const attempts = response?.attempts ?? [];
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_HISTORY_MUTATION,
          attempts,
        );
        return attempts;
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_ERROR_MUTATION,
          extractErrorMessages(
            response,
            "No se pudo consultar el historial de intentos.",
          ),
        );
        return [] as Array<SkillsSimulationHistoryAttempt>;
      });
  }

  @Action
  [Actions.RESUME_SKILLS_SIMULATION_SESSION](payload: {
    skill: string;
    sessionId: string;
  }) {
    // Reanuda un intento incompleto ya existente (detectado vía
    // historial) sin volver a llamar POST /session. GET .../menu
    // devuelve la misma forma (SessionMenuDto) que POST /session, así
    // que se reusa la misma mutación de sesión — se fuerza sessionId con
    // el que ya conocemos (viene del historial) por si la respuesta de
    // este endpoint puntual no lo incluyera.
    return SkillsSimulationService.getMenu(payload.sessionId)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_SKILLS_SIMULATION_SESSION_MUTATION, {
          ...data.data,
          sessionId: payload.sessionId,
          skill: payload.skill,
        });
        return data.data as SkillsSimulationMenuResponse;
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_SKILLS_SIMULATION_ERROR_MUTATION,
          extractErrorMessages(response, "No se pudo reanudar la evaluación."),
        );
        return undefined;
      });
  }

  @Action
  async [Actions.ENSURE_SKILLS_SIMULATION_SESSION](payload: { skill: string }) {
    const { skill } = payload;

    // Reemplaza el viejo patrón de las 3 vistas ("si no hay sesión de
    // este skill cargada, arrancar una a ciegas con
    // START_SKILLS_SIMULATION"). Ya no es seguro hacer eso: desde que el
    // backend soporta reintentar el simulacro completo, POST /session
    // dejó de ser un simple "traeme la sesión de este skill" —  si el
    // último intento ya está completo, crea uno NUEVO en vez de
    // devolver el viejo. Por eso hay que consultar primero el historial
    // (GET /skills-simulation/history) y decidir:
    //   - sin intentos todavía        -> crear el primero (START)
    //   - último intento incompleto   -> reanudarlo (RESUME, GET .../menu)
    //   - último intento completo     -> NO crear uno nuevo acá, solo
    //     cargar su resumen. Un intento nuevo solo se crea por acción
    //     explícita del talento (botón "Intentar de nuevo" en
    //     Results.vue, que dispatch-ea START_SKILLS_SIMULATION
    //     directamente) — nunca como efecto secundario de simplemente
    //     entrar a una pantalla del módulo.
    if (
      this.currentSkill === skill &&
      this.sessionId &&
      (!this.allPassed || this.summary)
    ) {
      // Ya hay una sesión cargada para este skill: si está en curso, o
      // si ya está completa y su resumen ya se cargó, no hace falta
      // volver a consultar el historial (evita un round-trip extra al
      // navegar entre pantallas del mismo skill, p.ej. Menu <-> Runner).
      return;
    }

    this.context.commit(Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION, true);
    try {
      const attempts = (await this.context.dispatch(
        Actions.GET_SKILLS_SIMULATION_HISTORY,
        { skill },
      )) as Array<SkillsSimulationHistoryAttempt>;

      if (!attempts || attempts.length === 0) {
        await this.context.dispatch(Actions.START_SKILLS_SIMULATION, {
          skill,
        });
        return;
      }

      const latest = attempts[0];
      if (!latest.allPassed) {
        await this.context.dispatch(Actions.RESUME_SKILLS_SIMULATION_SESSION, {
          skill,
          sessionId: latest.sessionId,
        });
        return;
      }

      this.context.commit(
        Mutations.SET_SKILLS_SIMULATION_COMPLETED_ATTEMPT_MUTATION,
        { skill, sessionId: latest.sessionId },
      );
      await this.context.dispatch(Actions.GET_SKILLS_SIMULATION_SUMMARY);
    } finally {
      this.context.commit(
        Mutations.SET_SKILLS_SIMULATION_LOADING_MUTATION,
        false,
      );
    }
  }
}
